import {Injectable} from '@angular/core';
import {AssessmentService} from './assessment.service';
import {map, of} from 'rxjs';

@Injectable()
export class AdjunctiveAssessmentService {
    configurations = {};

    constructor(private assessmentService: AssessmentService) {
        // SBC ACE
        this.configurations['SAN_BERNARDINO_ACE_SCORE_1.0'] = {
            show_top: ['aceScore'],
            hidden_categories: ['2'],
            refresh_questions: ['aceScore'],
            splash_display_rule: function (assessment) {
                return assessment.id === -1;
            },
            splash_style: {'text-align': 'left'},
            splash: '<b>Guidelines for Completion</b>\
                <ol><li>Please obtain this information from whomever (CSF worker, current caregiver, biological parent) or wherever (court report, medical record) you can obtain the best and most accurate information -– it can be from multiple sources.</li> \
                <li>Remember that this is about all the child’s life experiences, not just current situation.</li> \
                <li>\'Household\' should be interpreted as any and all households the child has lived in.</li> \
                <li>\'Member\' should be interpreted as anyone who has lived in the child’s household.</li> \
                <li>Question 6, \'Separation/Divorce\', should be interpreted to include the child’s experience of caregiver separation or divorce in any of the households they have lived in.   This includes situations in which a child in foster care experiences their biological parents\' separation through continued contact or visitation.<br> \
                <li>Question 7, \'Domestic Violence\', should be interpreted to include domestic violence towards any Maternal Caregiver.</li> \
                <li>Question 8, \'Substance Use\', should be interpreted to include illegal use of prescription drugs (e.g., Oxycontin<sup>TM</sup>).</li> \
                <li>If you have questions on how to fill out this form or how the information is collected, please inform your supervisor so that these concerns can be addressed.</li></ol>',
            related_required: this.aceAsqValidation
        };

        // SBC ASQ
        this.configurations['SAN_BERNARDINO_ASQ_1.0'] = {
            labels: [
                {
                    id: 'hideAsqCommunicationCategory',
                    reference: 'asqCommunicationScore',
                    colors: [{value: 'Borderline', color: 'grey'}, {
                        value: 'Negative',
                        color: 'white'
                    }, {value: 'Positive', color: 'black'}]
                },
                {
                    id: 'hideAsqGrossMotorCategory',
                    reference: 'asqGrossMotorScore',
                    colors: [{value: 'Borderline', color: 'grey'}, {
                        value: 'Negative',
                        color: 'white'
                    }, {value: 'Positive', color: 'black'}]
                },
                {
                    id: 'hideAsqFineMotorCategory',
                    reference: 'asqFineMotorScore',
                    colors: [{value: 'Borderline', color: 'grey'}, {
                        value: 'Negative',
                        color: 'white'
                    }, {value: 'Positive', color: 'black'}]
                },
                {
                    id: 'hideAsqProblemSolvingCategory',
                    reference: 'asqProblemSolvingScore',
                    colors: [{value: 'Borderline', color: 'grey'}, {
                        value: 'Negative',
                        color: 'white'
                    }, {value: 'Positive', color: 'black'}]
                },
                {
                    id: 'hideAsqPersonalSocialCategory',
                    reference: 'asqPersonalSocialScore',
                    colors: [{value: 'Borderline', color: 'grey'}, {
                        value: 'Negative',
                        color: 'white'
                    }, {value: 'Positive', color: 'black'}]
                },
                {
                    id: 'hideAsqSeCategory',
                    reference: 'asqSeScore',
                    colors: [{value: 'Borderline', color: 'grey'}, {
                        value: 'Negative',
                        color: 'white'
                    }, {value: 'Positive', color: 'black'}]
                }
            ],
            refresh_questions: [
                'hideAsqCommunicationCategory', 'hideAsqGrossMotorCategory', 'hideAsqFineMotorCategory', 'hideAsqProblemSolvingCategory',
                'hideAsqPersonalSocialCategory', 'hideAsqSeCategory'
            ],
            related_required: this.aceAsqValidation
        };

        // SBC BRIEFP
        this.configurations['SAN_BERNARDINO_BRIEFP_1.0'] = {
            non_editable: ['briefpInhibitoryRawScore', 'briefpFlexibilityRawScore', 'briefpMetacognitionRawScore', 'briefpGlobalExecutiveRawScore'],
            refresh_questions: ['briefpInhibitoryRawScore', 'briefpFlexibilityRawScore', 'briefpMetacognitionRawScore', 'briefpGlobalExecutiveRawScore']
        };

        // SBC DAYC2
        this.configurations['SAN_BERNARDINO_DAYC2_1.0'] = {
            labels: [
                {
                    id: 'hideDayc2CognitiveIntRange',
                    reference: 'dayc2CognitiveStdScore'
                },
                {
                    id: 'hideDayc2CommunicationIntRange',
                    reference: 'dayc2CommunicationStdScore'
                },
                {
                    id: 'hideDayc2SocialEmotionalIntRange',
                    reference: 'dayc2SocialEmotionalStdScore'
                },
                {
                    id: 'hideDayc2PhysicalDevelopmentIntRange',
                    reference: 'dayc2PhysicalDevelopmentStdScore'
                },
                {
                    id: 'hideDayc2AdaptiveBehaviorIntRange',
                    reference: 'dayc2AdaptiveBehaviorStdScore'
                },
                {
                    id: 'hideDayc2CompositeIntRange',
                    reference: 'dayc2CompositeStdScore'
                }
            ],
            refresh_questions: [
                'hideDayc2CognitiveIntRange', 'hideDayc2CommunicationIntRange', 'hideDayc2SocialEmotionalIntRange', 'hideDayc2PhysicalDevelopmentIntRange',
                'hideDayc2AdaptiveBehaviorIntRange', 'hideDayc2CompositeIntRange'
            ]
        };

        // SBC ECBI
        this.configurations['SAN_BERNARDINO_ECBI_1.0'] = {
            labels: [
                {
                    id: 'hideIntensityExceedsCutoff',
                    reference: 'intensityRawScore',
                    colors: [{value: 'No', color: 'white'}, {value: 'Yes', color: 'black'}]
                },
                {
                    id: 'hideProblemExceedsCutoff',
                    reference: 'problemRawScore',
                    colors: [{value: 'No', color: 'white'}, {value: 'Yes', color: 'black'}]
                }
            ],
            refresh_questions: [
                'hideIntensityExceedsCutoff', 'hideProblemExceedsCutoff'
            ]
        };

        // SBC PDMS2
        this.configurations['SAN_BERNARDINO_PDMS2_1.0'] = {
            non_editable: ['pdms2GrossMotorSum', 'pdms2FineMotorSum', 'pdms2TotalMotorSum'],
            refresh_questions: ['pdms2GrossMotorSum', 'pdms2FineMotorSum', 'pdms2TotalMotorSum']
        };

        // SBC PSISF
        this.configurations['SAN_BERNARDINO_PSISF_1.0'] = {
            non_editable: ['psisfTotalStressRawScore'],
            refresh_questions: ['psisfTotalStressRawScore']
        };

        // SBC SPMP
        this.configurations['SAN_BERNARDINO_SPMP_1.0'] = {
            labels: [
                {
                    id: 'hideSpmpSocialParticipationIntRange',
                    reference: 'spmpSocialParticipationTScore',
                    colors: [{value: 'Some Problems', color: 'grey'}, {
                        value: 'Typical',
                        color: 'white'
                    }, {value: 'Definite Dysfunction', color: 'black'}]
                },
                {
                    id: 'hideSpmpVisionIntRange',
                    reference: 'spmpVisionTScore',
                    colors: [{value: 'Some Problems', color: 'grey'}, {
                        value: 'Typical',
                        color: 'white'
                    }, {value: 'Definite Dysfunction', color: 'black'}]
                },
                {
                    id: 'hideSpmpHearingIntRange',
                    reference: 'spmpHearingTScore',
                    colors: [{value: 'Some Problems', color: 'grey'}, {
                        value: 'Typical',
                        color: 'white'
                    }, {value: 'Definite Dysfunction', color: 'black'}]
                },
                {
                    id: 'hideSpmpTouchIntRange',
                    reference: 'spmpTouchTScore',
                    colors: [{value: 'Some Problems', color: 'grey'}, {
                        value: 'Typical',
                        color: 'white'
                    }, {value: 'Definite Dysfunction', color: 'black'}]
                },
                {
                    id: 'hideSpmpTasteAndSmellIntRange',
                    reference: 'spmpTasteAndSmellTScore',
                    colors: [{value: 'Some Problems', color: 'grey'}, {
                        value: 'Typical',
                        color: 'white'
                    }, {value: 'Definite Dysfunction', color: 'black'}]
                },
                {
                    id: 'hideSpmpBodyAwarenessIntRange',
                    reference: 'spmpBodyAwarenessTScore',
                    colors: [{value: 'Some Problems', color: 'grey'}, {
                        value: 'Typical',
                        color: 'white'
                    }, {value: 'Definite Dysfunction', color: 'black'}]
                },
                {
                    id: 'hideSpmpBalanceMotionIntRange',
                    reference: 'spmpBalanceMotionTScore',
                    colors: [{value: 'Some Problems', color: 'grey'}, {
                        value: 'Typical',
                        color: 'white'
                    }, {value: 'Definite Dysfunction', color: 'black'}]
                },
                {
                    id: 'hideSpmpPlanningIdeasIntRange',
                    reference: 'spmpPlanningIdeasTScore',
                    colors: [{value: 'Some Problems', color: 'grey'}, {
                        value: 'Typical',
                        color: 'white'
                    }, {value: 'Definite Dysfunction', color: 'black'}]
                },
                {
                    id: 'hideSpmpTotalSensoryIntRange',
                    reference: 'spmpTotalSensoryTScore',
                    colors: [{value: 'Some Problems', color: 'grey'}, {
                        value: 'Typical',
                        color: 'white'
                    }, {value: 'Definite Dysfunction', color: 'black'}]
                }
            ],
            refresh_questions: [
                'hideSpmpSocialParticipationIntRange'
                , 'hideSpmpVisionIntRange'
                , 'hideSpmpHearingIntRange'
                , 'hideSpmpTouchIntRange'
                , 'hideSpmpTasteAndSmellIntRange'
                , 'hideSpmpBodyAwarenessIntRange'
                , 'hideSpmpBalanceMotionIntRange'
                , 'hideSpmpPlanningIdeasIntRange'
                , 'hideSpmpTotalSensoryIntRange'
                , 'spmpTotalSensoryRawScore'
            ],
            non_editable: ['spmpTotalSensoryRawScore'],
        };

        // SBC TSCYC
        this.configurations['SAN_BERNARDINO_TSCYC_1.0'] = {};
    }

    getConfiguration(type, majorVersion, minorVersion) {
        return this.configurations[type + '_' + majorVersion + '.' + minorVersion];
    }

    aceAsqValidation(assessment) {
        // Only check for new assessments
        if (assessment.id !== -1) {
            return of(false);
        } else {
            return this.assessmentService.countBeforeForEpisode(
                assessment.client.id,
                assessment.orgUnit.id,
                assessment.baseType,
                assessment.type,
                assessment.assessmentDate).pipe(
                map((res) => {
                        return res !== 0;
                    }
                ));
        }
    }
}
